import {
	Dialog,
	DialogContent,
	DialogTitle,
	LinearProgress,
	TextField,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { generateErrorState, handleValidateObj } from "helpers/utils";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { showError } from "../../../../redux/common/actions";
import EditDialogStyle from "styles/application/EditDialogStyle";
import * as yup from "yup";

const schema = yup.object({
	qty: yup
		.number()
		.typeError("Must be greater than 0")
		.min(1, "Must be greater than 0")
		.required("This field is required"),
	name: yup
		.string()
		.required("This field is required"),
	description: yup.string().nullable(),
});

const AED = EditDialogStyle();
const defaultError = { qty: null, name: null, description: null };

const useStyles = makeStyles()(() => ({
	dialogContent: {
		display: "flex",
		flexDirection: "column",
		minHeight: "300px",
		padding: "20px 24px",
	},
}));

const EditPartDialog = ({ open, handleClose, editData, updateHandler, captions }) => {
	const { t } = useTranslation();
	const { classes } = useStyles();
	const [input, setInput] = useState({ qty: 1, name: "", description: "" });
	const [errors, setErrors] = useState(defaultError);
	const [isUpdating, setIsUpdating] = useState(false);
	const dispatch = useDispatch();

	useEffect(() => {
		if (editData && open) {
			setInput({
				qty: editData.qty,
				name: editData.name,
				description: editData.description ?? "",
			});
		}
	}, [editData, open]);

	const closeOverride = () => {
		setErrors(defaultError);
		handleClose();
	};

	const handleSave = async () => {
		setIsUpdating(true);
		try {
			const localChecker = await handleValidateObj(schema, input);
			if (!localChecker.some((el) => el.valid === false)) {
				await updateHandler(editData.id, {
					name: input.name,
					description: input.description || null,
					qty: Number(input.qty),
				});
				closeOverride();
			} else {
				setErrors({ ...errors, ...generateErrorState(localChecker) });
			}
		} catch (err) {
			dispatch(
				showError(
					t("validation.failedToEditEntity", {
						entity: captions?.part ?? "part",
					})
				)
			);
			closeOverride();
		}
		setIsUpdating(false);
	};

	const handleEnterPress = (e) => {
		if (!isUpdating && e.keyCode === 13) {
			handleSave();
		}
	};

	return (
		<Dialog
			open={open}
			onClose={closeOverride}
			aria-labelledby="edit-part-title"
			maxWidth="sm"
			fullWidth
		>
			{isUpdating ? <LinearProgress /> : null}
			<AED.ActionContainer>
				<DialogTitle id="edit-part-title">
					<AED.HeaderText>
						{t("title.editEntity", { entity: captions?.part ?? "Part" })}
					</AED.HeaderText>
				</DialogTitle>
				<AED.ButtonContainer>
					<AED.CancelButton onClick={closeOverride} variant="contained">
						{t("action.cancel")}
					</AED.CancelButton>
					<AED.ConfirmButton onClick={handleSave} variant="contained" disabled={isUpdating}>
						{t("action.save")}
					</AED.ConfirmButton>
				</AED.ButtonContainer>
			</AED.ActionContainer>
			<DialogContent className={classes.dialogContent}>
				<div style={{ marginBottom: "10px" }}>
					<TextField
						label={captions?.partQuantity ?? "Quantity"}
						error={errors.qty !== null}
						helperText={errors.qty}
						fullWidth
						value={input.qty}
						onChange={(e) => setInput({ ...input, qty: e.target.value })}
						onKeyDown={handleEnterPress}
						type="number"
						autoFocus
					/>
				</div>
				<div style={{ marginBottom: "10px" }}>
					<TextField
						label={captions?.partName ?? "Part Name"}
						error={errors.name !== null}
						helperText={errors.name}
						fullWidth
						value={input.name}
						onChange={(e) => setInput({ ...input, name: e.target.value })}
						onKeyDown={handleEnterPress}
					/>
				</div>
				<div style={{ marginBottom: "10px" }}>
					<TextField
						label={captions?.partDescription ?? "Part Description"}
						fullWidth
						multiline
						rows={4}
						value={input.description ?? ""}
						onChange={(e) => setInput({ ...input, description: e.target.value })}
						onKeyDown={handleEnterPress}
					/>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default EditPartDialog;
